/**
 * User Details Page
 * Redesigned to match UserProfilePage / EmployeeDetailsPage visual language:
 * Hero header with gradient, stat cards, SectionCards — no tabs.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Store as ProviderIcon,
  Security as SecurityIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LinkOff,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import { BindingFormModal } from './components';
import {
  PageShell,
  IdentityBanner,
  IdentifierChip,
  StatCard,
  SectionCard,
  InfoItem,
} from '../../components';

// Services and types
import { userService } from '../../services/userService';
import { getActiveRoles } from '../../services/roleService';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { Role } from '../../types/rbac.types';
import {
  User,
  UserBinding,
  UserRole,
  BindingFormData,
  BindingEntityType,
  BindingFunction,
  AddBindingRequest,
  USER_STATUS_LABELS,
  getUserStatusColor,
  BINDING_ENTITY_TYPE_LABELS,
  BINDING_STATUS_LABELS,
  formatCPF,
  formatWhatsApp,
  getBindingFunctionLabel,
  getBindingEntityName,
  getRoleLabel,
} from '../../types/user';
import { useAcao } from '../../authorization/useVeredito';
import { ActionButton, ControleIconButton } from '../../components/common';

/* ══════════════════════════════════════════════════════════════ */

/** A carga fica fora do componente para não trocar de identidade a cada render. */
const carregarPapeis = async (): Promise<readonly Role[]> => {
  const resposta = await getActiveRoles();

  return resposta.data ?? [];
};

const UserDetailsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const vincular = useAcao('user.update', 'Vincular');
  const removerVinculo = useAcao('user.update', 'Remover vínculo');
  const gerenciarPapeis = useAcao('user.manage-roles', 'Gerenciar papéis');

  const [user, setUser] = useState<User | null>(null);
  const [bindings, setBindings] = useState<UserBinding[]>([]);
  const [papelEscolhido, setPapelEscolhido] = useState<number | ''>('');
  const [salvandoPapel, setSalvandoPapel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingBindings, setLoadingBindings] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Binding modal state
  const [bindingModalOpen, setBindingModalOpen] = useState(false);
  const [bindingLoading, setBindingLoading] = useState(false);
  const [bindingError, setBindingError] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bindingToDelete, setBindingToDelete] = useState<UserBinding | null>(null);

  // Load user data
  useEffect(() => {
    const loadUser = async () => {
      if (!id) {
        setError('ID do usuário não fornecido');
        setLoading(false);
        return;
      }

      try {
        const userData = await userService.getUserById(parseInt(id));
        setUser(userData);
      } catch (err: any) {
        const errorMessage = err.message ?? 'Erro ao carregar usuário';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id, enqueueSnackbar]);

  // Load bindings
  useEffect(() => {
    const loadBindings = async () => {
      if (!id) return;

      try {
        setLoadingBindings(true);
        const userBindings = await userService.getUserBindings(parseInt(id));
        setBindings(userBindings);
      } catch (err: any) {
        enqueueSnackbar('Erro ao carregar vínculos', { variant: 'error' });
      } finally {
        setLoadingBindings(false);
      }
    };

    loadBindings();
  }, [id, enqueueSnackbar]);

  const handleEdit = () => navigate(`/users/${id}/edit`);
  const handleBack = () => navigate('/users');

  /**
   * Os papéis que existem, para o seletor. Quem não pode conceder nem carrega a lista — o card
   * vira só a leitura dos papéis que a pessoa já tem. A lista pede `role.read`, que a porta
   * desta tela não cobre, e por isso passa pelo gancho.
   */
  const papeis = useListaPermitida<Role>('role.read', 'Papéis', carregarPapeis, {
    habilitado: !gerenciarPapeis.disabled,
  });
  const papeisDisponiveis = papeis.itens;

  /**
   * Conceder e remover devolvem a lista nova do backend, e é ela que vai para a tela: assim o
   * que aparece é o que o servidor gravou, não o que a tela supôs.
   */
  const atualizarPapeis = (papeis: UserRole[]) => {
    setUser((anterior) => (anterior ? { ...anterior, roles: papeis } : anterior));
  };

  const handleConcederPapel = async () => {
    if (!id || papelEscolhido === '') return;

    setSalvandoPapel(true);
    try {
      const papeis = await userService.assignRole(parseInt(id), papelEscolhido);
      atualizarPapeis(papeis);
      setPapelEscolhido('');
      enqueueSnackbar('Papel concedido com sucesso!', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao conceder o papel', { variant: 'error' });
    } finally {
      setSalvandoPapel(false);
    }
  };

  const handleRemoverPapel = async (roleId: number) => {
    if (!id) return;

    setSalvandoPapel(true);
    try {
      const papeis = await userService.removeRole(parseInt(id), roleId);
      atualizarPapeis(papeis);
      enqueueSnackbar('Papel removido.', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao remover o papel', { variant: 'error' });
    } finally {
      setSalvandoPapel(false);
    }
  };

  // Handle add binding
  const handleAddBinding = async (data: BindingFormData) => {
    if (!id) return;

    setBindingLoading(true);
    setBindingError(null);

    try {
      const request: AddBindingRequest = {
        entityType: data.entityType as BindingEntityType,
        entityId: parseInt(data.entityId),
        function: data.function as BindingFunction,
      };

      await userService.addUserBinding(parseInt(id), request);
      enqueueSnackbar('Vínculo adicionado com sucesso!', { variant: 'success' });

      const userBindings = await userService.getUserBindings(parseInt(id));
      setBindings(userBindings);
      setBindingModalOpen(false);
    } catch (err: any) {
      const errorMessage = err.message ?? 'Erro ao adicionar vínculo';
      setBindingError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setBindingLoading(false);
    }
  };

  // Handle remove binding
  const handleRemoveBinding = async (bindingId: number) => {
    if (!id) return;

    try {
      await userService.removeUserBinding(parseInt(id), bindingId);
      enqueueSnackbar('Vínculo removido com sucesso!', { variant: 'success' });

      const userBindings = await userService.getUserBindings(parseInt(id));
      setBindings(userBindings);
    } catch (err: any) {
      const errorMessage = err.message ?? 'Erro ao remover vínculo';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Delete confirmation handlers
  const handleDeleteClick = (binding: UserBinding) => {
    setBindingToDelete(binding);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (bindingToDelete) {
      handleRemoveBinding(bindingToDelete.id);
    }
    setDeleteDialogOpen(false);
    setBindingToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBindingToDelete(null);
  };

  // Get entity icon
  const getEntityIcon = (entityType: BindingEntityType) => {
    return entityType === BindingEntityType.COMPANY ? (
      <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
    ) : (
      <ProviderIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
    );
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const activeBindingsCount = bindings.filter((b) => b.status === 'active').length;

  const breadcrumbItems = [
    { label: 'Início', path: '/' },
    { label: 'Usuários', path: '/users' },
    { label: user?.name || 'Carregando...' },
  ];

  // Loading state
  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbItems}>
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <PageShell
        breadcrumbs={breadcrumbItems}
        actions={
          <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={handleBack}>
            Voltar
          </Button>
        }
      >
        <Alert severity="error">{error || 'Usuário não encontrado'}</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      actions={
        <>
          <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={handleBack}>
            Voltar
          </Button>
          <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={handleEdit}>
            Editar
          </Button>
        </>
      }
    >
        {/* ═══ IDENTIDADE ═══ */}
        <IdentityBanner
          avatar={
            user.avatarPath ? (
              <Box
                component="img"
                src={user.avatarPath}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              getInitials(user.name)
            )
          }
          name={user.name}
          status={
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              <Chip
                label={USER_STATUS_LABELS[user.situacao]}
                color={getUserStatusColor(user.situacao)}
                size="small"
              />
              {user.roles?.map((role) => (
                <Chip key={role.id} label={getRoleLabel(role.name)} size="small" color="primary" />
              ))}
            </Box>
          }
          context={user.email}
          identifiers={
            <IdentifierChip icon={<BadgeIcon sx={{ fontSize: 16 }} />} label={formatCPF(user.cpf)} />
          }
        />

        {/* ═══ STAT CARDS ═══ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<BusinessIcon fontSize="small" />}
              label="Vínculos Ativos"
              value={loadingBindings ? '...' : String(activeBindingsCount)}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<BusinessIcon fontSize="small" />}
              label="Total Vínculos"
              value={loadingBindings ? '...' : String(bindings.length)}
              color={theme.palette.secondary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<CalendarIcon fontSize="small" />}
              label="Cadastrado em"
              value={user.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-BR') : '—'}
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<CalendarIcon fontSize="small" />}
              label="Última Atualização"
              value={user.updatedAt ? new Date(user.updatedAt).toLocaleDateString('pt-BR') : '—'}
              color={theme.palette.warning.main}
            />
          </Grid>
        </Grid>

        {/* ═══ DETAIL SECTIONS ═══ */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {/* Personal Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Informações Pessoais"
              icon={<PersonIcon fontSize="small" />}
              accentColor={theme.palette.primary.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    icon={<PersonIcon sx={{ fontSize: 14 }} />}
                    label="Nome"
                    value={user.name}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    icon={<EmailIcon sx={{ fontSize: 14 }} />}
                    label="E-mail"
                    value={user.email}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    icon={<BadgeIcon sx={{ fontSize: 14 }} />}
                    label="CPF"
                    value={formatCPF(user.cpf)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    icon={<PhoneIcon sx={{ fontSize: 14 }} />}
                    label="WhatsApp"
                    value={user.whatsapp ? formatWhatsApp(user.whatsapp) : undefined}
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          {/* Bindings */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Vínculos"
              icon={<BusinessIcon fontSize="small" />}
              accentColor={theme.palette.secondary.main}
              action={
                <ActionButton
                  acao="user.update"
                  rotulo="Vincular"
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setBindingModalOpen(true)}
                >
                  Vincular
                </ActionButton>
              }
            >
              {loadingBindings ? (
                <Box>
                  {[1, 2].map((i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Skeleton variant="text" width="50%" />
                      <Skeleton variant="text" width="70%" />
                    </Paper>
                  ))}
                </Box>
              ) : bindings.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={1}>
                  {bindings.map((binding) => (
                    <Paper
                      key={binding.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.secondary.main, 0.02),
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          {getEntityIcon(binding.entityType)}
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {getBindingEntityName(binding)}
                          </Typography>
                        </Box>
                        <ControleIconButton
                          controle={removerVinculo}
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(binding)}
                        >
                          <DeleteIcon fontSize="small" />
                        </ControleIconButton>
                      </Box>
                      <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
                        <Chip
                          label={BINDING_ENTITY_TYPE_LABELS[binding.entityType]}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={binding.functionLabel || getBindingFunctionLabel(binding.function)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={BINDING_STATUS_LABELS[binding.status]}
                          size="small"
                          color={binding.status === 'active' ? 'success' : 'default'}
                        />
                      </Box>
                      {binding.entityDetails?.cnpj && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          CNPJ: {binding.entityDetails.cnpj}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Desde: {new Date(binding.startedAt).toLocaleDateString('pt-BR')}
                      </Typography>
                      {binding.endedAt && (
                        <Typography variant="caption" color="error" display="block">
                          Encerrado em: {new Date(binding.endedAt).toLocaleDateString('pt-BR')}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box textAlign="center" py={2}>
                  <LinkOff sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Esta pessoa ainda não faz parte de nenhuma equipe. É o vínculo que lhe dá
                    acesso aos dados de uma empresa ou clínica.
                  </Typography>
                  <ActionButton
                    acao="user.update"
                    rotulo="Vincular"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setBindingModalOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Vincular
                  </ActionButton>
                  {vincular.disabled && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                      Peça a quem administra os acessos para vincular esta pessoa.
                    </Typography>
                  )}
                </Box>
              )}
            </SectionCard>
          </Grid>

          {/* Roles */}
          <Grid size={{ xs: 12 }}>
            <SectionCard
              title="Papéis"
              icon={<SecurityIcon fontSize="small" />}
              accentColor={theme.palette.info.main}
            >
              <Box display="flex" gap={1} flexWrap="wrap">
                {(user.roles ?? []).map((role) => (
                  <Chip
                    key={role.id}
                    label={role.displayName || getRoleLabel(role.name)}
                    size="small"
                    color="primary"
                    icon={<SecurityIcon sx={{ fontSize: '14px !important' }} />}
                    onDelete={
                      gerenciarPapeis.disabled ? undefined : () => void handleRemoverPapel(role.id)
                    }
                    disabled={salvandoPapel}
                  />
                ))}
                {(user.roles ?? []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Sem papel nenhum — esta pessoa entra no sistema e não enxerga nada.
                  </Typography>
                )}
              </Box>

              {!gerenciarPapeis.disabled && (
                <Box display="flex" gap={1} alignItems="flex-start" sx={{ mt: 2 }} flexWrap="wrap">
                  <TextField
                    select
                    size="small"
                    label="Conceder papel"
                    value={papelEscolhido}
                    onChange={(evento) => setPapelEscolhido(Number(evento.target.value))}
                    disabled={salvandoPapel || papeisDisponiveis.length === 0}
                    sx={{ minWidth: 260 }}
                    helperText={papeis.bloqueio ?? 'Para apurar denúncias do Canal, conceda Comitê de Denúncias.'}
                  >
                    {papeisDisponiveis
                      .filter((papel) => !(user.roles ?? []).some((atual) => atual.id === papel.id))
                      .map((papel) => (
                        <MenuItem key={papel.id} value={papel.id}>
                          {papel.displayName}
                        </MenuItem>
                      ))}
                  </TextField>
                  <ActionButton
                    acao="user.manage-roles"
                    rotulo="Conceder papel"
                    variant="contained"
                    size="medium"
                    onClick={() => void handleConcederPapel()}
                    disabled={salvandoPapel || papelEscolhido === ''}
                  >
                    Conceder
                  </ActionButton>
                </Box>
              )}
            </SectionCard>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Confirmar Remoção</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja remover o vínculo com{' '}
              <strong>{bindingToDelete ? getBindingEntityName(bindingToDelete) : ''}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              O usuário perderá o acesso aos recursos associados a este vínculo.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancelar</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Remover
            </Button>
          </DialogActions>
        </Dialog>

        {/* Binding Form Modal */}
        <BindingFormModal
          open={bindingModalOpen}
          onClose={() => {
            setBindingModalOpen(false);
            setBindingError(null);
          }}
          onSubmit={handleAddBinding}
          loading={bindingLoading}
          error={bindingError}
          userName={user.name}
          existingBindings={bindings}
        />
    </PageShell>
  );
};

export default UserDetailsPage;
